import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCache } from '@/composables/useCache'
import { EventRepository } from '@/infrastructure/repositories/event.repository'
import { createApiClient } from '@/infrastructure/api/client'
import type { Event } from '@/domain/entities/event.entity'
import type { GetEventsParams } from '@/domain/repositories/event.repository'
import { ErrorHandler, type SerializableError } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'

const DEFAULT_PAGE_SIZE = 9

export const useEventStore = defineStore('event', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('event')
  const events = ref<Event[]>([])
  const loading = ref(false)
  const currentLimit = ref(DEFAULT_PAGE_SIZE)
  const lastParams = ref<GetEventsParams | null>(null)
  const localeEventsCache = useCache<boolean>()
  const localesWithEvents = localeEventsCache.cache

  const apiClient = createApiClient({
    baseUrl: useRuntimeConfig().public.NUXT_APP_LANDING_API,
  })
  const eventRepository = new EventRepository(apiClient)

  const hasEvents = computed(() => events.value.length > 0)
  const hasMore = computed(() => events.value.length >= currentLimit.value)

  async function fetchEvents(params?: GetEventsParams, append: boolean = false) {
    loading.value = true
    clearErrors()

    const limit = params?.limit ?? DEFAULT_PAGE_SIZE
    if (!append) {
      currentLimit.value = limit
    }

    const requestParams: GetEventsParams = {
      event_locale: params?.event_locale,
      limit,
    }
    lastParams.value = { ...requestParams }

    try {
      const result = await eventRepository.findMany(requestParams)
      if (append) {
        events.value = [...events.value, ...result]
      } else {
        events.value = result
      }
      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  async function loadMore(limit: number = DEFAULT_PAGE_SIZE) {
    const nextLimit = currentLimit.value + limit
    await fetchEvents(
      { ...lastParams.value, limit: nextLimit },
      false
    )
  }

  function reset() {
    events.value = []
    currentLimit.value = DEFAULT_PAGE_SIZE
    lastParams.value = null
    loading.value = false
    clearErrors()
  }

  function clearError() {
    clearErrors()
  }

  async function checkLocaleHasEvents(locale: string): Promise<boolean> {
    if (!locale) return false
    return localeEventsCache.getOrFetch(locale, async () => {
      try {
        // 1) Пробуем локализованный запрос (по аналогии со статьями).
        const localized = await eventRepository.findMany({ event_locale: locale, limit: 1 })
        if (localized.length > 0) return true

        // 2) Fallback: некоторые бэкенды событий не фильтруют по локали.
        const anyLocale = await eventRepository.findMany({ limit: 1 })
        return anyLocale.length > 0
      } catch {
        // На случай, если event_locale не поддерживается сервером.
        try {
          const anyLocale = await eventRepository.findMany({ limit: 1 })
          return anyLocale.length > 0
        } catch {
          return false
        }
      }
    })
  }

  return {
    events,
    loading,
    error,
    currentLimit,
    hasEvents,
    hasError,
    hasMore,
    fetchEvents,
    loadMore,
    reset,
    clearError,
    localesWithEvents,
    checkLocaleHasEvents,
  }
})

